import BasicModal from '@/shared/ui/Modal'
import { PlusIcon } from '@/shared/ui'
import { Box, Button, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { MAX_DEVICE_ALIAS } from '@/shared/constants'
import { useState } from 'react'
import { RoundedInputField } from '@/shared/ui/RoundedInputField'

interface Props {
    isModalOpen: boolean
    deviceId?: string | null
    alias?: string | null
    closeModal: () => void
    setAlias: (deviceId?: string | null, alias?: string | null) => void
}

const DeviceSetAliasModal = ({
    isModalOpen,
    closeModal,
    alias,
    deviceId,
    setAlias,
}: Props) => {
    const [newAlias, setNewAlias] = useState<string | null | undefined>(alias)
    const theme = useTheme()
    const { t: translate } = useTranslation()

    const styles = {
        modalContainer: {
            maxWidth: { md: '40vw' },
            maxHeight: '60vw',
            width: 'calc(100% - 10px)',
        },
        cancelButton: {
            marginTop: '12px',
            width: '100%',
            height: '36px',
            padding: '8px 12px',
            gap: '8px',
            borderRadius: '10px',
            bgcolor: 'transparent',
            border: '1px solid white',
            color: 'white',
        },
        confirmButton: {
            marginTop: '12px',
            width: '100%',
            height: '36px',
            padding: '8px 12px',
            gap: '8px',
            borderRadius: '10px',
            bgcolor: 'primary.main',
        },
        inputField: {
            background: 'none',
            width: '100%',
            padding: '10px',
            borderRadius: '8px',
            border: '1px solid grey',
            color: 'white',
            marginTop: '5px',
            outline: 'none',
        },
    }

    const handleInputChange = (value: string) => {
        if (value.length <= MAX_DEVICE_ALIAS) setNewAlias(value)
    }

    const handleModalClose = () => {
        closeModal()
        setNewAlias(null)
    }

    const handleAliasSave = () => {
        setAlias(deviceId, newAlias)
        setNewAlias(null)
    }

    return (
        <BasicModal
            isOpen={isModalOpen}
            setIsModalOpen={handleModalClose}
            styles={styles.modalContainer}>
            <Button
                onClick={handleModalClose}
                sx={{ transform: 'rotate(45deg)' }}>
                <PlusIcon fill={theme.palette.primary.main} />
            </Button>
            <Box width="100%" sx={{ color: '#fff' }}>
                <Typography variant="label3" mb="8px" color="white">
                    {translate('settingsPageDeviceSetAliasModalTitle')}
                </Typography>
                <RoundedInputField
                    native={{ type: 'text', name: 'alias' }}
                    sx={styles.inputField}
                    placeholder={
                        alias || translate('settingsPageTableHeadYourName')
                    }
                    searchValue={newAlias || ''}
                    setSearchValue={handleInputChange}
                />
                <Box display="flex" columnGap="16px" mt="16px">
                    <Button
                        data-testid="accept-button"
                        disabled={!deviceId}
                        sx={styles.confirmButton}
                        onClick={handleAliasSave}>
                        <Typography color="white" variant="label4">
                            {translate('accept')}
                        </Typography>
                    </Button>
                    <Button
                        data-testid="cancel-button"
                        variant="outlined"
                        sx={styles.cancelButton}
                        onClick={handleModalClose}>
                        <Typography color="white" variant="label4">
                            {translate('globalCancel')}
                        </Typography>
                    </Button>
                </Box>
            </Box>
        </BasicModal>
    )
}

export { DeviceSetAliasModal }
